type AssistantRouteDomain =
  | "dashboard"
  | "producers"
  | "projects"
  | "finance"
  | "crm"
  | "settings"
  | "unknown";

export type AssistantRouteContext = {
  organizationSlug: string | null;
  domain: AssistantRouteDomain;
  recordType: "producer" | "project" | null;
  recordId: string | null;
  pathname: string;
};

export function getAssistantRecordPath({
  organizationSlug,
  recordType,
  recordId,
}: {
  organizationSlug: string;
  recordType: "producer" | "project";
  recordId: string;
}) {
  const collection = recordType === "producer" ? "producers" : "projects";

  return `/${encodeURIComponent(organizationSlug)}/${collection}/${encodeURIComponent(recordId)}`;
}

export function getAssistantRouteContext(pathname: string): AssistantRouteContext {
  const segments = pathname.split("/").filter(Boolean);
  const organizationSlug = segments[0] ?? null;
  const section = segments[1];
  const recordId = segments[2] ?? null;

  if (!organizationSlug) {
    return {
      organizationSlug: null,
      domain: "unknown",
      recordType: null,
      recordId: null,
      pathname,
    };
  }

  if (!section) {
    return {
      organizationSlug,
      domain: "dashboard",
      recordType: null,
      recordId: null,
      pathname,
    };
  }

  if (section === "producers") {
    return {
      organizationSlug,
      domain: "producers",
      recordType: recordId && recordId !== "new" ? "producer" : null,
      recordId: recordId && recordId !== "new" ? recordId : null,
      pathname,
    };
  }

  if (section === "projects") {
    return {
      organizationSlug,
      domain: "projects",
      recordType: recordId && recordId !== "new" ? "project" : null,
      recordId: recordId && recordId !== "new" ? recordId : null,
      pathname,
    };
  }

  const knownDomains = new Set(["financial", "crm", "settings"]);
  const domain =
    section === "financial"
      ? "finance"
      : knownDomains.has(section)
        ? (section as AssistantRouteDomain)
        : "unknown";

  return {
    organizationSlug,
    domain,
    recordType: null,
    recordId: null,
    pathname,
  };
}
